import { FormLabel } from 'opensolar-ui'
import { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import { Form, FormSpy } from 'react-final-form'
import { ResourcePrefix, useFileUploadContext } from '../controller/UploadFileContext'
import { FileTagsChipsInput } from '../FileTagsChipsInput'
import { InputRow } from '../styles'
import { http } from '../../services/http'
import { fileTagTypeForResourcePrefix } from '../controller/privateFileTagUtils'
import { AutoUploadFileTags } from './AutoUploadFileTags'

type Props = {
  resource_prefix: ResourcePrefix
  slot: string
  disabled?: boolean
  defaultFileTags?: string[]
  defaultFileTagTitles?: string[]
  orgId?: number
  hideTags?: boolean
}

export const UploadFileTags = ({
  resource_prefix,
  slot,
  disabled,
  defaultFileTags,
  defaultFileTagTitles,
  orgId,
  hideTags = false,
}: Props) => {
  const translate = useTranslate()
  const { updateFileTags } = useFileUploadContext()
  const previousFileTagsRef = useRef<any>(null)
  const [initialTagUrls, setInitialTagUrls] = useState<string[]>(defaultFileTags || [])

  useEffect(() => {
    if (hideTags) {
      return
    }
    if (defaultFileTags?.length) {
      setInitialTagUrls(defaultFileTags)
      updateFileTags(slot, defaultFileTags)
      return
    }
    if (!defaultFileTagTitles?.length || !orgId) {
      return
    }
    let cancelled = false
    const tagType = fileTagTypeForResourcePrefix(resource_prefix)
    http.os
      .get({
        url: `file_tags/?type=${tagType}&per_page=200`,
      })
      .then((res) => {
        if (cancelled) return
        const tags = res.data?.results ?? res.data ?? []
        const urls = defaultFileTagTitles
          .map((title) => tags.find((tag: { title?: string; url?: string }) => tag.title === title)?.url)
          .filter((url): url is string => Boolean(url))
        if (urls.length) {
          setInitialTagUrls(urls)
          updateFileTags(slot, urls)
        }
      })
      .catch((error) => {
        console.error('Failed to resolve default file tag titles:', error)
      })
    return () => {
      cancelled = true
    }
  }, [defaultFileTags, defaultFileTagTitles, hideTags, orgId, resource_prefix, slot, updateFileTags])

  if (hideTags) {
    return (
      <AutoUploadFileTags slot={slot} defaultFileTagTitles={defaultFileTagTitles} />
    )
  }

  return (
    <Form
      key={initialTagUrls.join('|')}
      onSubmit={() => {
        return {}
      }}
      initialValues={{ file_tags: initialTagUrls }}
      render={() => {
        return (
          <>
            <FormSpy
              subscription={{ values: true }}
              render={({ values }) => {
                const currentFileTags = values?.file_tags
                if (currentFileTags !== previousFileTagsRef.current) {
                  previousFileTagsRef.current = currentFileTags
                  if (currentFileTags) {
                    const tagUrls = Array.isArray(currentFileTags) ? currentFileTags : []
                    updateFileTags(slot, tagUrls)
                  }
                }
                return null
              }}
            />
            <InputRow>
              <FormLabel>{translate('Tags')}</FormLabel>
              <FileTagsChipsInput resource_prefix={resource_prefix} disabled={disabled} />
            </InputRow>
          </>
        )
      }}
    />
  )
}
